import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/use-reduced-motion'

interface ParallaxImageProps {
    src: string
    alt: string
    className?: string
    priority?: boolean
}

// A gentle scroll drift (transform only). Visitors who prefer reduced motion get a still image.
export const ParallaxImage: FC<ParallaxImageProps> = ({
    src,
    alt,
    className,
    priority = false
}): ReactNode => {
    const frameRef = useRef<HTMLDivElement>(null)
    const reducedMotion = usePrefersReducedMotion()

    const { scrollYProgress } = useScroll({
        target: frameRef,
        offset: ['start end', 'end start']
    })

    const y = useTransform(scrollYProgress, [0, 1], ['-7%', '7%'])

    return (
        <div
            ref={frameRef}
            className={cn('relative overflow-hidden', className)}
        >
            <motion.img
                src={src}
                alt={alt}
                loading={priority ? 'eager' : 'lazy'}
                decoding='async'
                style={reducedMotion ? undefined : { y }}
                className='pointer-events-none absolute inset-x-0 top-[-10%] h-[120%] w-full max-w-none object-cover'
            />
        </div>
    )
}
